import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, FileDown, Loader2, Zap, Wand2, Search, Sparkles } from "lucide-react";
import { fetchProperties, createProperty, updateProperty, deleteProperty, downloadBrochure, setPropertyApproval, oneClickCampaign, listingLookup, generateListing } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import AddressAutocomplete from "../AddressAutocomplete";
import PostcodeAddressPicker from "../PostcodeAddressPicker";
import MediaDropzone from "./MediaDropzone";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const empty = {
  title: "", slug: "", address: "", postcode: "", price: 0, priceQualifier: "Offers in Region of",
  status: "for-sale", type: "Terraced House", bedrooms: 2, bathrooms: 1, receptions: 1, featured: false,
  images: "", floorplans: "", features: "", summary: "", description: "", epc: "C", video: "", sqft: "", epcDocs: "",
};

const EPC_GRADES = ["A", "B", "C", "D", "E", "F", "G"];

const TYPES = ["Apartment", "Terraced House", "Semi-Detached House", "Detached House", "Town House", "House"];

const AdminProperties = () => {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [cityFilter, setCityFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const refresh = () => fetchProperties({}).then(setList).catch(() => setList([]));
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setIntel(null); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setIntel(null);
    setForm({ ...p, sqft: p.sqft || "", images: (p.images || []).join("\n"), floorplans: (p.floorplans || []).join("\n"), epcDocs: (p.epcDocs || []).join("\n"), features: (p.features || []).join("\n") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      sqft: form.sqft ? Number(form.sqft) : null,
      bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), receptions: Number(form.receptions),
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      floorplans: (form.floorplans || "").split("\n").map((s) => s.trim()).filter(Boolean),
      epcDocs: (form.epcDocs || "").split("\n").map((s) => s.trim()).filter(Boolean),
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      slug: form.slug || slugify(form.title),
    };
    try {
      if (editing) {
        await updateProperty(editing.id, payload);
        toast({ title: "Property updated" });
      } else {
        await createProperty(payload);
        toast({ title: "Property created" });
      }
      setOpen(false); refresh();
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "Please check the fields." });
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await deleteProperty(p.id); toast({ title: "Property deleted" }); refresh(); }
    catch { toast({ title: "Delete failed" }); }
  };

  const toggleApproval = async (p) => {
    const next = p.approval === "draft" ? "approved" : "draft";
    try {
      await setPropertyApproval(p.id, next);
      setList((ls) => ls.map((x) => x.id === p.id ? { ...x, approval: next } : x));
      toast({ title: next === "approved" ? "Listing approved — live" : "Listing set to draft" });
    } catch { toast({ title: "Couldn't update approval" }); }
  };

  const [campaigning, setCampaigning] = useState(null);
  const oneClick = async (p) => {
    setCampaigning(p.id);
    try { const r = await oneClickCampaign(p.id); toast({ title: "Campaign drafted", description: `${r.count} drafts (post, ad, email) queued to Pending Review in Marketing.` }); }
    catch { toast({ title: "Campaign failed", description: "Please try again." }); }
    finally { setCampaigning(null); }
  };

  const [looking, setLooking] = useState(false);
  const [intel, setIntel] = useState(null);
  const autofill = async () => {
    if (!form.postcode) { toast({ title: "Enter a postcode first" }); return; }
    setLooking(true); setIntel(null);
    try {
      const r = await listingLookup({ postcode: form.postcode, address: form.address || "", type: form.type, bedrooms: Number(form.bedrooms) || 3, bathrooms: Number(form.bathrooms) || 1 });
      setForm((prev) => ({
        ...prev,
        sqft: prev.sqft ? prev.sqft : (r.sqft || prev.sqft),
        price: (!prev.price || Number(prev.price) === 0) && r.suggestedPrice ? r.suggestedPrice : prev.price,
      }));
      setIntel(r);
      toast({ title: "Property facts loaded", description: `${r.sqft ? r.sqft + " sq ft · " : ""}${r.suggestedPrice ? "Suggested £" + r.suggestedPrice.toLocaleString() : ""}` });
    } catch (err) { toast({ title: "Lookup failed", description: err?.response?.data?.detail || "Try a full postcode." }); }
    finally { setLooking(false); }
  };

  const [genning, setGenning] = useState(false);
  const generate = async () => {
    if (!form.postcode && !form.title) { toast({ title: "Add a postcode or title first" }); return; }
    setGenning(true);
    try {
      const photoCount = form.images.split("\n").map((s) => s.trim()).filter(Boolean).length;
      const r = await generateListing({
        title: form.title, address: form.address, postcode: form.postcode, type: form.type,
        status: form.status, bedrooms: Number(form.bedrooms) || 0, bathrooms: Number(form.bathrooms) || 0,
        receptions: Number(form.receptions) || 0, sqft: Number(form.sqft) || null, epc: form.epc,
        price: Number(form.price) || null,
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        photoCount,
      });
      setForm((prev) => ({
        ...prev,
        summary: r.summary || prev.summary,
        description: [r.description, (r.highlights || []).length ? "\nKey highlights:\n" + r.highlights.map((h) => `• ${h}`).join("\n") : ""].filter(Boolean).join("\n"),
        features: (r.features && r.features.length ? r.features : prev.features.split("\n").filter(Boolean)).join("\n"),
      }));
      toast({ title: "AI listing generated", description: `${(r.highlights || []).length} highlights, ${(r.features || []).length} features.` });
    } catch (err) { toast({ title: "Generation failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setGenning(false); }
  };

  const inputCls = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  const matchesCity = (p) => {
    if (cityFilter === "all") return true;
    const pc = (p.postcode || "").toUpperCase().replace(/\s/g, "");
    const text = `${p.address || ""} ${p.title || ""} ${p.town || ""}`.toLowerCase();
    return cityFilter === "bath" ? (pc.startsWith("BA") || text.includes("bath")) : (pc.startsWith("BS") || text.includes("bristol"));
  };
  const visible = list.filter(matchesCity);

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">Property Management ({visible.length})</h3>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full bg-[#faf7f2] border border-[#e2d8c8] p-1" data-testid="admin-city-filter">
            {[["all", "All"], ["bath", "Bath"], ["bristol", "Bristol"]].map(([v, label]) => (
              <button key={v} onClick={() => setCityFilter(v)} data-testid={`admin-city-${v}`}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-500 transition-colors ${cityFilter === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a]"}`}>{label}</button>
            ))}
          </div>
          <button onClick={openNew} data-testid="add-property-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> Add Property</button>
        </div>
      </div>
      <div className="space-y-2">
        {visible.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
            <div className="flex items-center gap-3">
              <img src={p.images?.[0]} alt="" className="w-14 h-14 rounded-lg object-cover" />
              <div>
                <p className="text-[14px] font-500 text-[#2b2b2b]">{p.title}</p>
                <p className="text-[12px] text-[#a0a0a0]">{p.type} · £{p.price.toLocaleString()} · {p.status === "to-rent" ? "To Let" : "For Sale"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleApproval(p)} data-testid="approval-toggle" title="Toggle listing approval" className={`px-2.5 py-1 rounded-full text-[11px] font-500 border transition-colors ${p.approval === "draft" ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100" : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"}`}>{p.approval === "draft" ? "Draft" : "Live"}</button>
              <button onClick={() => oneClick(p)} disabled={campaigning === p.id} data-testid="one-click-campaign-btn" title="One-click campaign (post + ad + email)" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{campaigning === p.id ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Campaign</button>
              <button onClick={() => downloadBrochure(p.slug, false)} title="Brochure PDF" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-[#6a6a6a] hover:text-[#b8935f] hover:border-[#b8935f] transition-colors"><FileDown size={15} /></button>
              <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-[#6a6a6a] hover:text-[#b8935f] hover:border-[#b8935f] transition-colors"><Pencil size={15} /></button>
              <button onClick={() => remove(p)} className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-red-500 hover:border-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[620px] max-h-[85vh] overflow-y-auto">
          <DialogTitle className="sr-only">{editing ? "Edit Property" : "Add Property"}</DialogTitle>
          <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">{editing ? "Edit Property" : "Add Property"}</h2>
          <form onSubmit={save} className="space-y-3">
            <input required placeholder="Title (e.g. Duke Street, Bath, BA2)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} className={inputCls} />
            <div className="rounded-lg bg-[#faf7f2] border border-[#f0ece5] p-3">
              <p className="text-[12px] font-500 text-[#6a6a6a] mb-2">Find the full address from a postcode</p>
              <PostcodeAddressPicker
                postcode={form.postcode}
                address={form.address}
                compact
                testid="property-address"
                onPick={({ address, postcode, sqft }) => setForm((prev) => ({ ...prev, ...(address ? { address, title: prev.title || address } : {}), ...(postcode ? { postcode } : {}), ...(sqft ? { sqft } : {}) }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                onSelect={({ address, postcode, lat, lng }) => setForm((prev) => ({ ...prev, address, postcode: postcode || prev.postcode, lat: lat ?? prev.lat, lng: lng ?? prev.lng }))}
                placeholder="…or type address for map pin"
              />
              <input placeholder="Postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className={inputCls} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={autofill} disabled={looking} data-testid="autofill-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#1c1c1e] text-white text-[12px] font-500 hover:bg-black transition-colors disabled:opacity-60">
                {looking ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Auto-fill facts from postcode
              </button>
              {intel && (
                <span className="text-[12px] text-[#6a6a6a]" data-testid="lookup-intel">
                  {intel.sqft ? `${intel.sqft.toLocaleString()} sq ft` : "—"}{intel.suggestedPrice ? ` · est. £${intel.suggestedPrice.toLocaleString()}` : ""}{intel.floodRisk ? ` · flood ${intel.floodRisk}` : ""}{intel.avgAreaPrice ? ` · area avg £${intel.avgAreaPrice.toLocaleString()}` : ""}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="for-sale">For Sale</SelectItem><SelectItem value="to-rent">To Rent</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <input placeholder="Price qualifier" value={form.priceQualifier} onChange={(e) => setForm({ ...form, priceQualifier: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <input type="number" placeholder="Beds" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={inputCls} />
              <input type="number" placeholder="Baths" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={inputCls} />
              <input type="number" placeholder="Receptions" value={form.receptions} onChange={(e) => setForm({ ...form, receptions: e.target.value })} className={inputCls} />
              <input type="number" placeholder="Sq ft" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} className={inputCls} data-testid="sqft-input" />
            </div>
            <MediaDropzone label="Photos (drag to reorder — first is the cover)" value={form.images} onChange={(v) => setForm((f) => ({ ...f, images: v }))} testid="photos" coverLabel="Cover" />
            <MediaDropzone label="Floorplans" value={form.floorplans} onChange={(v) => setForm((f) => ({ ...f, floorplans: v }))} accept="image/*,application/pdf" testid="floorplans" />
            <MediaDropzone label="EPC certificate / graph" value={form.epcDocs} onChange={(v) => setForm((f) => ({ ...f, epcDocs: v }))} accept="image/*,application/pdf" testid="epc-docs" />
            <div className="flex items-center justify-between pt-1">
              <label className="text-[13px] font-500 text-[#6a6a6a]">Listing copy</label>
              <button type="button" onClick={generate} disabled={genning} data-testid="generate-listing-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-[#b8935f] to-[#a67f4d] text-white text-[12px] font-500 hover:opacity-90 transition-opacity disabled:opacity-60">
                {genning ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate with AI
              </button>
            </div>
            <input placeholder="Short summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={inputCls} data-testid="summary-input" />
            <textarea placeholder="Full description — or generate with AI after adding photos" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={inputCls + " h-auto py-2"} data-testid="description-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.epc || "C"} onValueChange={(v) => setForm({ ...form, epc: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="epc-select"><SelectValue placeholder="EPC" /></SelectTrigger>
                <SelectContent>{EPC_GRADES.map((g) => <SelectItem key={g} value={g}>EPC {g}</SelectItem>)}</SelectContent>
              </Select>
              <input placeholder="Video tour URL (embed)" value={form.video || ""} onChange={(e) => setForm({ ...form, video: e.target.value })} className={inputCls} data-testid="video-input" />
            </div>
            <textarea placeholder="Key features (one per line)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={3} className={inputCls + " h-auto py-2"} />
            <label className="flex items-center gap-2 text-[14px] text-[#4a4a4a]"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#b8935f]" /> Featured listing</label>
            <button type="submit" disabled={saving} className="w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{saving ? "Saving..." : editing ? "Update Property" : "Create Property"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProperties;
